"use client"

import * as React from "react"

import { useState } from "react";

import { LogIn, Verified } from "lucide-react";
import { useTranslations } from "next-intl";

import { createClient } from '@/utils/supabase/client';
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "./input-otp";
import { Button } from "./button";
import { toast } from "sonner";
import { useUserStore } from "@/stores/userStore/useUserStore";
import { Provider } from "@supabase/supabase-js";
import { useShallow } from "zustand/shallow";

const LoginForm = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => {
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const t = useTranslations('Login');
    const supabase = createClient();
    const { getUserProfileFromSupabase, updateUser } = useUserStore(useShallow(state => ({ getUserProfileFromSupabase: state.getUserProfileFromSupabase, updateUser: state.updateUser })))
    const router = useRouter();
    const [otp, setOtp] = useState<string>("");
    // Get the 'next' parameter from URL
    const getNextParam = () => {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            let nextParam = urlParams.get('next') || '/';

            console.log('Raw next param:', nextParam);

            // Handle potentially encoded URLs
            try {
                // Decode until we get a stable result (handles double encoding)
                let decoded = nextParam;
                let previousDecoded = '';

                while (decoded !== previousDecoded && (decoded.includes('%2F') || decoded.includes('%3F') || decoded.includes('%3D'))) {
                    previousDecoded = decoded;
                    decoded = decodeURIComponent(decoded);
                }

                nextParam = decoded;
                console.log('Decoded next param:', nextParam);
            } catch (e) {
                console.warn('Failed to decode next parameter:', e);
            }

            return nextParam;
        }
        return '/';
    };


    const [showOTP, setShowOTP] = useState(false);

    const [OTPDisabled, setOTPDisabled] = useState(false);

    React.useEffect(() => {
        const nextParam = getNextParam();
        if (nextParam.startsWith('/invitations?code=')) {
            const code = nextParam.split('=')[1];

            if (!code) {
                setShowOTP(true);
                return;
            }

            if (code.length !== 6) {
                setShowOTP(true);
                return;
            }

            // if invitation code is provided
            setShowOTP(false);

        }
        setShowOTP(true);
    }, []);

    const handleDiscordLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const nextParam = getNextParam();
            const callbackUrl = new URL(`${window.location.origin}/auth/callback`);
            callbackUrl.searchParams.set('next', nextParam);

            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'discord',
                options: {
                    redirectTo: callbackUrl.toString(),
                },
            });

            if (error) throw error;
        } catch (error: unknown) {
            setError(error instanceof Error ? error.message : 'An error occurred');
            setIsLoading(false);
        }
    };

    const handleGoogleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const nextParam = getNextParam();
            const callbackUrl = new URL(`${window.location.origin}/auth/callback`);
            callbackUrl.searchParams.set('next', nextParam);

            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: callbackUrl.toString(),
                    queryParams: {
                        prompt: 'select_account'
                    }
                },
            });

            const userProfile = await getUserProfileFromSupabase()
            console.log("user profile", userProfile)

            setTimeout(() => { }, 10000)

            if (!userProfile) return

            updateUser(userProfile)

            if (error) throw error;
        } catch (error: unknown) {
            setError(error instanceof Error ? error.message : 'An error occurred');
            setIsLoading(false);
        }
    };

    const handleQuickEmailLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        //https://localhost:3000/invitations?code=000051

        try {
            const nextParam = getNextParam();

            console.log("nextParam", nextParam)

            if (nextParam.startsWith('/invitations?code=')) {
                const code = nextParam.split('=')[1];
                let invitationCode: string = code
                invitationCode = code;
                console.log("invitationCode", invitationCode);


                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const callbackUrl = new URL(`${window.location.origin}/auth/callback`);
                callbackUrl.searchParams.set('next', nextParam);

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { data, error } = await supabase.auth.signUp({
                    email: `${invitationCode}@gmail.com`,
                    password: invitationCode,
                    options: {
                        emailRedirectTo: callbackUrl.toString(),
                    },
                })

                const { data: dataSignIn, error: errorSignIn } = await supabase.auth.signInWithPassword({
                    email: `${invitationCode}@gmail.com`,
                    password: invitationCode,
                })

                if (errorSignIn) {
                    console.warn(errorSignIn);
                    throw new Error('Failed to sign in with invitation code');
                }

                // Redirect to the original next parameter (which should be the invitations page)
                router.replace(nextParam)

                console.log("data is: ", dataSignIn)
            }
        } catch (error: unknown) {
            setError(error instanceof Error ? error.message : 'An error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={cn("w-full max-w-sm bg-linear-to-b from-card/50 to-card  rounded-3xl shadow-xl shadow-opacity-10 p-8 flex flex-col items-center border", className)} {...props}>
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-white mb-6 shadow-lg shadow-opacity-5">
                <LogIn className="w-7 h-7 text-black" />
            </div>
            <h2 className="text-2xl font-semibold mb-2 text-center">
                {t('greeting')}
            </h2>
            {error ? (
                <p className="text-red-500/70 text-sm mb-6 text-center" role="alert">
                    {error}
                </p>
            ) : <p className="text-gray-500 text-sm mb-6 text-center">
                {t('title')}
            </p>}



            {showOTP && (
                <div className="flex justify-center items-center w-full my-2 gap-x-2">
                    <div className="text-xs opacity-50">邀请码:</div>
                    <InputOTP
                        maxLength={6}
                        className="w-full"
                        value={otp}
                        onChange={async (value) => {
                            console.log("OTP value:", value);
                            setOtp(value);
                            if (value.length === 6) {
                                setOTPDisabled(true);
                                console.log("OTP value:", value);
                                toast.info("验证中...")

                                try {
                                    // First try to sign in
                                    const { data: dataSignIn, error: errorSignIn } = await supabase.auth.signInWithPassword({
                                        email: `${value}@gmail.com`,
                                        password: value,
                                    })

                                    if (errorSignIn) {
                                        // If sign in fails, try to sign up first
                                        console.log("Sign in failed, attempting sign up:", errorSignIn);

                                        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                                            email: `${value}@gmail.com`,
                                            password: value,
                                        })

                                        if (signUpError) {
                                            console.error("Sign up failed:", signUpError);
                                            toast.error("验证失败，请检查邀请码");
                                            setOTPDisabled(false);
                                            return;
                                        }

                                        // After successful sign up, try to sign in again
                                        const { data: dataSignInRetry, error: errorSignInRetry } = await supabase.auth.signInWithPassword({
                                            email: `${value}@gmail.com`,
                                            password: value,
                                        })

                                        if (errorSignInRetry) {
                                            console.error("Sign in failed after sign up:", errorSignInRetry);
                                            toast.error("验证失败，请联系管理员");
                                            setOTPDisabled(false);
                                            return;
                                        }

                                        console.log("Successfully signed up and signed in:", dataSignInRetry);
                                    } else {
                                        console.log("Successfully signed in:", dataSignIn);
                                    }

                                    // Use the next parameter if it contains an invitation code
                                    const nextParam = getNextParam();
                                    if (nextParam.includes('invitations') && nextParam.includes('code=')) {
                                        router.replace(nextParam);
                                    } else {
                                        router.replace(`/invitations?code=${value}`)
                                    }

                                } catch (error: unknown) {
                                    console.error("Unexpected error:", error);
                                    toast.error("验证失败，请联系管理员");
                                    setOTPDisabled(false);
                                }
                            }
                        }}
                        disabled={isLoading || OTPDisabled}
                    >
                        <InputOTPGroup>
                            <InputOTPSlot index={0} className="text-foreground" />
                            <InputOTPSlot index={1} className="text-foreground" />
                            <InputOTPSlot index={2} className="text-foreground" />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                            <InputOTPSlot index={3} className="text-foreground" />
                            <InputOTPSlot index={4} className="text-foreground" />
                            <InputOTPSlot index={5} className="text-foreground" />
                        </InputOTPGroup>
                    </InputOTP>
                </div>
            )}
            <div className="flex items-center w-full my-2">
                <div className="grow border-t border-dashed border-gray-200"></div>
                <span className="mx-2 text-xs text-gray-400">{t('continuing')}{' '}{t('terms')}{' '}{t('policy')}</span>
                <div className="grow border-t border-dashed border-gray-200"></div>
            </div>

            <div className={cn("flex gap-3 w-full items-center mt-2", showOTP ? "justify-end" : "justify-center")}>
                {!showOTP ? <Button onClick={handleQuickEmailLogin} size={showOTP ? "icon" : "default"} className="bg-white p-2" disabled={isLoading}>
                    <Verified className="w-6 h-6" />
                </Button> : <div className="text-foreground text-xs opacity-50 md:text-base">其他方式:</div>}
                <Button size={showOTP ? "icon" : "default"} onClick={handleDiscordLogin} className="bg-white p-2" disabled={isLoading}>
                    <img
                        src="https://www.svgrepo.com/show/353655/discord-icon.svg"
                        alt="Discord"
                        className="w-6 h-6"
                    />
                </Button>
                <Button size={showOTP ? "icon" : "default"} onClick={handleGoogleLogin} className="bg-white p-2" disabled={isLoading}>
                    <img
                        src="https://www.svgrepo.com/show/475656/google-color.svg"
                        alt="Google"
                        className="w-6 h-6"
                    />
                </Button>
            </div>
        </div>
    );
};

export { LoginForm };